import { Color, Object3D, PerspectiveCamera } from "three";
import { float, mix, renderOutput, saturate, select, uniform, vec3, vec4 } from "three/tsl";
import { ao } from "three/examples/jsm/tsl/display/GTAONode.js";
import { outline } from "three/examples/jsm/tsl/display/OutlineNode.js";
import { fxaa } from "three/examples/jsm/tsl/display/FXAANode.js";
import type {
  TslEffect,
  TslEffectContext,
} from "../../../../engine/postprocessing/PostProcessingController";

export type AoEffectOptions = {
  radius?: number;
  samples?: number;
  scale?: number;
  thickness?: number;
  distanceExponent?: number;
  distanceFallOff?: number;
  resolutionScale?: number;
  intensity?: number;
  useTemporalFiltering?: boolean;
};

function asColorNode(input: unknown): unknown {
  if (
    input &&
    typeof (input as { getTextureNode?: () => unknown }).getTextureNode ===
      "function"
  ) {
    return (input as { getTextureNode: () => unknown }).getTextureNode();
  }
  return input;
}

/**
 * GTAO — owned by gtao-* Lit entities.
 *
 * Beauty * AO with normals reconstructed from depth (no MRT normal buffer).
 * Sky/clear pixels are masked so beauty * ao does not crush the background
 * to black (three's ao() only discards depth>=1; reversed-Z clear is ~0).
 */
export class AoTslEffect implements TslEffect {
  readonly order = 10;
  readonly branch = "main" as const;
  /** Depth-only AO — skip MRT normals; GTAONode uses getNormalFromDepth. */
  readonly needsNormal = false;

  private _radius: number;
  private _samples: number;
  private _scale: number;
  private _thickness: number;
  private _distanceExponent: number;
  private _distanceFallOff: number;
  private _resolutionScale: number;
  private readonly _intensity = uniform(1);
  private _useTemporalFiltering: boolean;
  /** Last composed ao() node — setParameters updates its uniforms live. */
  private _aoNode: ReturnType<typeof ao> | undefined;

  constructor(options: AoEffectOptions = {}) {
    // Defaults match RenderSettingsStore GTAO presets.
    this._radius = options.radius ?? 0.25;
    this._samples = options.samples ?? 32;
    this._scale = options.scale ?? 1;
    this._thickness = options.thickness ?? 1;
    this._distanceExponent = options.distanceExponent ?? 2.5;
    this._distanceFallOff = options.distanceFallOff ?? 1;
    this._resolutionScale = options.resolutionScale ?? 1;
    this._intensity.value = options.intensity ?? 1;
    this._useTemporalFiltering = options.useTemporalFiltering ?? false;
  }

  setParameters(partial: AoEffectOptions): void {
    if (partial.radius != null) this._radius = partial.radius;
    if (partial.samples != null) this._samples = partial.samples;
    if (partial.scale != null) this._scale = partial.scale;
    if (partial.thickness != null) this._thickness = partial.thickness;
    if (partial.distanceExponent != null)
      this._distanceExponent = partial.distanceExponent;
    if (partial.distanceFallOff != null)
      this._distanceFallOff = partial.distanceFallOff;
    if (partial.resolutionScale != null)
      this._resolutionScale = partial.resolutionScale;
    if (partial.intensity != null) this._intensity.value = partial.intensity;
    if (partial.useTemporalFiltering != null)
      this._useTemporalFiltering = partial.useTemporalFiltering;
    this._syncAoNode();
  }

  private _syncAoNode(): void {
    const aoNode = this._aoNode;
    if (!aoNode) return;
    aoNode.radius.value = this._radius;
    aoNode.samples.value = this._samples;
    aoNode.scale.value = this._scale;
    aoNode.thickness.value = this._thickness;
    aoNode.distanceExponent.value = this._distanceExponent;
    aoNode.distanceFallOff.value = this._distanceFallOff;
    aoNode.resolutionScale = this._resolutionScale;
    aoNode.useTemporalFiltering = this._useTemporalFiltering;
  }

  compose(input: unknown, ctx: TslEffectContext): unknown {
    // null normal → GTAONode reconstructs from depth (same as WebGL path).
    const aoNode = ao(ctx.depth as never, null as never, ctx.camera);
    this._aoNode = aoNode;
    this._syncAoNode();

    const aoTex = aoNode.getTextureNode();
    const depthR = (
      ctx.depth as {
        r: {
          greaterThan: (v: unknown) => unknown;
          lessThan: (v: unknown) => unknown;
        };
      }
    ).r;

    const geometryMask = ctx.reversedDepth
      ? depthR.greaterThan(float(1e-5))
      : depthR.lessThan(float(1).sub(float(1e-5)));

    const occluded = mix(vec3(1), vec3(aoTex.r), this._intensity);
    const aoFactor = select(geometryMask as never, occluded, vec3(1));
    return (asColorNode(input) as { mul: (v: unknown) => unknown }).mul(
      vec4(aoFactor, 1),
    );
  }
}

export type OutlineEffectOptions = {
  edgeThickness?: number;
  edgeStrength?: number;
  edgeColor?: number;
};

/**
 * Outline — owned by outline-postprocess Lit entity.
 *
 * Overlay branch returns vec4(edgeRgb, edgeAlpha) so the controller can
 * alpha-over onto beauty — additive merge washes out on bright / empty BG.
 * OutlineNode stays in the TSL graph even when nothing is selected so
 * select/deselect does not rebuild SSGI/TRAA (that flicker-resets history).
 */
export class OutlineTslEffect implements TslEffect {
  readonly order = 20;
  readonly branch = "overlay" as const;

  /** Stable array shared with OutlineNode — mutate in place on selection change. */
  readonly selectedObjects: Object3D[] = [];

  private readonly _edgeThickness = uniform(1.5);
  private readonly _edgeGlow = uniform(0.2);
  private readonly _edgeStrength = uniform(4);
  private readonly _edgeOpacity = uniform(0.45);
  private readonly _visibleEdgeColor = uniform(new Color(0x009587));
  private readonly _hiddenEdgeColor = uniform(new Color(0x009587));
  private _outlineNode: ReturnType<typeof outline> | undefined;

  constructor(options: OutlineEffectOptions = {}) {
    this._edgeThickness.value = options.edgeThickness ?? 1.5;
    this._edgeStrength.value = options.edgeStrength ?? 4;
    const c = new Color(options.edgeColor ?? 0x009587);
    this._visibleEdgeColor.value.copy(c);
    this._hiddenEdgeColor.value.copy(c);
  }

  setEdgeStyle(thickness: number, strength: number): void {
    this._edgeThickness.value = thickness;
    this._edgeStrength.value = strength;
  }

  /**
   * Replace selection in place. Does not require a TSL graph rebuild.
   */
  setSelectedObjects(objects: Object3D[]): boolean {
    this.selectedObjects.length = 0;
    for (const obj of objects) this.selectedObjects.push(obj);
    if (this._outlineNode) {
      this._outlineNode.selectedObjects = this.selectedObjects;
    }
    return false;
  }

  /** Keep overlay in the graph so enter/leave selection does not rebuild. */
  isActive(): boolean {
    return true;
  }

  compose(input: unknown, _ctx: TslEffectContext): unknown {
    return input;
  }

  private _disposeOutlineNode(): void {
    if (!this._outlineNode) return;
    // dispose() clears selectedObjects — detach shared array first.
    this._outlineNode.selectedObjects = [];
    this._outlineNode.dispose();
    this._outlineNode = undefined;
  }

  composeOverlay(ctx: TslEffectContext): unknown | null {
    if (
      !this._outlineNode ||
      this._outlineNode.scene !== ctx.scene ||
      this._outlineNode.camera !== ctx.camera
    ) {
      this._disposeOutlineNode();
      this._outlineNode = outline(ctx.scene, ctx.camera, {
        selectedObjects: this.selectedObjects,
        edgeThickness: this._edgeThickness,
        edgeGlow: this._edgeGlow,
      });
    } else {
      this._outlineNode.selectedObjects = this.selectedObjects;
    }

    const { visibleEdge, hiddenEdge } = this._outlineNode;
    // X-ray: occluded + visible edges share one mask, then alpha-over solid color.
    const edgeWeight = (
      visibleEdge as {
        add: (v: unknown) => {
          mul: (v: unknown) => {
            saturate: () => { mul: (v: unknown) => unknown };
          };
        };
      }
    )
      .add(hiddenEdge)
      .mul(this._edgeStrength)
      .saturate()
      .mul(this._edgeOpacity);

    return vec4(this._visibleEdgeColor, edgeWeight as never);
  }
}

/**
 * AA for sugar-taa on WebGPU: FXAA (2D / when SSGI is off).
 * SSGI temporal filtering owns TRAA on its own composite — do not use TRAA here
 * (velocity MRT + camera jitter blacks out ortho/2D).
 */
export class TraaTslEffect implements TslEffect {
  readonly order = 90;
  readonly branch = "post" as const;
  readonly needsVelocity = false;
  readonly needsOutputColorTransform = false;

  compose(input: unknown, _ctx: TslEffectContext): unknown {
    return fxaa(renderOutput(input as never) as never);
  }
}

export function isOutlineTslEffect(
  effect: TslEffect,
): effect is OutlineTslEffect {
  return effect instanceof OutlineTslEffect;
}
